// src/pages/HomePage.js

import { useState, useEffect } from "react";
import styled from "styled-components";
import SEO from "../components/SEO";
import Categories from "../components/Categories";
import Products from "../components/Products";
import Newsletter from "../components/Newsletter";
import Footer from "../components/Footer";

import Slider from "../components/Slider";

/* ---------------- Sidebar Styles ---------------- */

const SidebarContainer = styled.div`
  width: 240px;
  position: fixed;
  top: 100px;
  left: 0;
  height: calc(100vh - 100px);
  overflow-y: auto;
  border-right: 2px solid #ccc;
  background: #f9f9f9;
  padding: 1rem;
  z-index: 1000;

  @media only screen and (max-width: 768px) {
    top: 60px;
    height: calc(100vh - 60px);
    width: 75%;
    max-width: 300px;
    transform: translateX(${(props) => (props.open ? "0" : "-100%")});
    transition: transform 0.3s ease;
    z-index: 1500;
    box-shadow: ${(props) =>
      props.open ? "4px 0 10px rgba(0,0,0,0.2)" : "none"};
  }
`;

/* Overlay backdrop — only shows on mobile when sidebar is open */
const Backdrop = styled.div`
  display: none;

  @media only screen and (max-width: 768px) {
    display: ${(props) => (props.open ? "block" : "none")};
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1400;
  }
`;

/* Toggle button to show stores on mobile — hidden on desktop */
const StoresToggle = styled.button`
  display: none;

  @media only screen and (max-width: 768px) {
    display: block;
    position: sticky;
    top: 60px;
    margin: 10px;
    padding: 10px 16px;
    background: teal;
    color: white;
    border: none;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    z-index: 900;
  }
`;

const StoreButton = styled.button`
  display: block;
  width: 100%;
  margin-bottom: 12px;
  padding: 10px 16px;
  border-radius: 30px;
  background: #e0e0e0;
  text-align: center;
  border: 1px solid #bbb;
  cursor: pointer;
  color: #333;

  &:hover {
    background: #d4d4d4;
  }
`;

/* ---------------- Page Layout ---------------- */

const PageWrapper = styled.div`
  margin-left: 260px;
  margin-top: 100px;
  padding-bottom: 40px;

  @media only screen and (max-width: 768px) {
    margin-left: 0;
    margin-top: 60px;
  }
`;

const TabsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
  display: flex;
  gap: 10px;
  border-bottom: 1px solid #ddd;
`;

const Tab = styled.button`
  padding: 10px 20px;
  border: none;
  border-bottom: 3px solid
    ${(props) => (props.active ? "teal" : "transparent")};
  background-color: transparent;
  cursor: pointer;
  font-size: 16px;
  font-weight: ${(props) => (props.active ? "600" : "400")};
  color: ${(props) => (props.active ? "teal" : "#555")};
  transition: all 0.2s ease;

  &:hover {
    color: teal;
  }
`;

const SectionWrapper = styled.div``;

/* ---------------- HomePage Component ---------------- */

const HomePage = () => {
  const [activeTab, setActiveTab] = useState("cars");
  const [users, setUsers] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.title = "SAUDIDUNES — Marketplace";
  }, []);

  /* Fetch real users from backend */
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch("/api/auth/users");
        const data = await res.json();
        setUsers(data);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    fetchUsers();
  }, []);

  /* Open store in a NEW browser window */
  const openStore = (user) => {
    const url = `/store/${user._id}`;
    setSidebarOpen(false);
    window.open(url, "_blank");
  };

  return (
    <div>

     <SEO
        title="SaudiDune — Cars, Real Estate & Trading Tools in Saudi Arabia"
        description="Marketplace for cars and real estate across Saudi Arabia, plus live stock charts, GIS flood analysis, CAD tools and more."
      />  
      
      {/* Mobile-only: button to open stores sidebar */}
      <StoresToggle onClick={() => setSidebarOpen(true)}>
        🏪 Browse Stores
      </StoresToggle>

      {/* Mobile-only: backdrop when sidebar is open */}
      <Backdrop open={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      {/* Sidebar — always rendered, visibility controlled by CSS */}
      <SidebarContainer open={sidebarOpen}>
        {users.map((user) => (
          <StoreButton key={user._id} onClick={() => openStore(user)}>
            🏪 {user.name} — {user.storeName}
          </StoreButton>
        ))}
      </SidebarContainer>

      {/* Main Page */}
      <PageWrapper>
        <TabsContainer>
          <Tab
            active={activeTab === "cars"}
            onClick={() => setActiveTab("cars")}
          >
            Cars
          </Tab>
          <Tab
            active={activeTab === "realestate"}
            onClick={() => setActiveTab("realestate")}
          >
            Real Estate
          </Tab>
        </TabsContainer>

        <SectionWrapper>
          <Slider mode={activeTab} />
          <Categories mode={activeTab} />
          <Products mode={activeTab} />
          <Newsletter />
          <Footer />
        </SectionWrapper>
      </PageWrapper>
    </div>
  );
};

export default HomePage;
